// src/services/detection/DetectionService.js
import * as tf from '@tensorflow/tfjs';
import * as cocoSsd from '@tensorflow-models/coco-ssd';
import { DETECTION_MODELS, DETECTION_CONFIG } from './config';

class DetectionService {
  constructor() {
    this.detector = null;
    this.customDetectors = new Map();
    this.isInitialized = false;
    this.isLoading = false;
    this.currentModel = null;
    this.config = { ...DETECTION_CONFIG };
    this.listeners = new Set();
    
    // Smoothing buffer for stable detections
    this.detectionHistory = [];
    this.maxHistoryLength = 5;
  }

  subscribe(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  notify(event, data) {
    this.listeners.forEach(callback => callback(event, data));
  }

  updateConfig(newConfig) {
    this.config = { ...this.config, ...newConfig };
  }

  registerDetector(name, detectorInstance) {
    this.customDetectors.set(name, detectorInstance);
    console.log(`Registered custom detector: ${name}`);
  }

  unregisterDetector(name) {
    const detector = this.customDetectors.get(name);
    if (detector && detector.dispose) {
      detector.dispose();
    }
    this.customDetectors.delete(name);
  }

  async initialize(modelType = DETECTION_CONFIG.defaultModel) {
    if (this.isLoading) {
      console.log('Model already loading...');
      return false;
    }

    if (this.customDetectors.has(modelType)) {
      const customDetector = this.customDetectors.get(modelType);
      await customDetector.load();
      this.detector = customDetector;
      this.currentModel = modelType;
      this.isInitialized = true;
      this.notify('ready', { modelType });
      return true;
    }

    if (this.isInitialized && this.currentModel === modelType) {
      return true;
    }

    this.isLoading = true;
    this.notify('loading', { modelType });

    try {
      await tf.ready();
      console.log('TensorFlow.js backend:', tf.getBackend());

      if (this.detector && this.detector.dispose) {
        this.detector.dispose();
        this.detector = null;
      }

      this.detector = await this.loadModel(modelType);
      this.currentModel = modelType;
      this.isInitialized = true;
      this.isLoading = false;

      this.notify('ready', { modelType });
      console.log(`Detection model loaded: ${modelType}`);
      
      return true;
    } catch (error) {
      this.isLoading = false;
      this.notify('error', { error: error.message });
      console.error('Failed to initialize detection:', error);
      throw error;
    }
  }

  async loadModel(modelType) {
    switch (modelType) {
      case DETECTION_MODELS.COCO_SSD:
        // COCO-SSD is better for detecting objects like shoes
        // when full body pose isn't visible
        return await cocoSsd.load({
          base: 'mobilenet_v2'
        });

      default:
        throw new Error(`Unknown model type: ${modelType}`);
    }
  }

  async detect(videoElement) {
    if (!this.isInitialized || !this.detector) {
      throw new Error('Detection service not initialized');
    }

    if (!videoElement || videoElement.readyState < 2) {
      return null;
    }

    try {
      let results;

      if (this.currentModel === DETECTION_MODELS.COCO_SSD) {
        results = await this.detectWithCocoSsd(videoElement);
      } else if (this.customDetectors.has(this.currentModel)) {
        const rawResults = await this.detector.detect(videoElement);
        results = { feet: rawResults, timestamp: Date.now() };
      }

      // Apply smoothing
      if (this.config.enableSmoothing) {
        results = this.smoothResults(results);
      }

      this.notify('detection', results);
      return results;
    } catch (error) {
      this.notify('error', { error: error.message });
      throw error;
    }
  }

  async detectWithCocoSsd(videoElement) {
    const { videoWidth, videoHeight } = videoElement;
    const predictions = await this.detector.detect(videoElement);
    
    // Filter for relevant objects (shoes, feet-like objects)
    // COCO-SSD doesn't have a "shoe" class, but we can use heuristics
    const relevantClasses = ['person', 'sports ball', 'bottle'];
    
    const feet = [];
    const { lowerLegMode, colors } = this.config;
    
    // Strategy 1: Look for person detections in lower portion
    const personDetections = predictions.filter(p => p.class === 'person');
    
    if (personDetections.length > 0) {
      // If we detect partial person (likely legs/feet), use those boxes
      personDetections.forEach((detection, idx) => {
        const [x, y, width, height] = detection.bbox;
        
        // Determine if this is left or right based on position
        const centerX = x + width / 2;
        const side = centerX < videoWidth / 2 ? 'right' : 'left';
        
        feet.push({
          id: `person-${idx}-${side}`,
          side,
          confidence: detection.score,
          boundingBox: { x, y, width, height },
          color: colors[side] || colors.default,
          source: 'coco-ssd-person'
        });
      });
    }
    
    // Strategy 2: If no person detected, use region-based detection
    if (feet.length === 0) {
      const regionBasedFeet = this.detectByRegion(videoElement, predictions);
      feet.push(...regionBasedFeet);
    }
    
    // Strategy 3: If still nothing, create default regions for manual adjustment
    if (feet.length === 0 && this.config.lowerLegMode.enabled) {
      const defaultFeet = this.createDefaultRegions(videoWidth, videoHeight);
      feet.push(...defaultFeet);
    }

    return {
      feet,
      timestamp: Date.now(),
      frameSize: { width: videoWidth, height: videoHeight },
      rawPredictions: predictions
    };
  }

  detectByRegion(videoElement, predictions) {
    const { videoWidth, videoHeight } = videoElement;
    const { lowerLegMode, colors } = this.config;
    const feet = [];

    // Analyze image regions for foot-like shapes
    // This is a simplified approach - a custom model would be better
    
    const regions = [
      { side: 'right', ...lowerLegMode.expectedRegions.right },
      { side: 'left', ...lowerLegMode.expectedRegions.left }
    ];

    regions.forEach(region => {
      // Check if any detection falls within expected region
      const regionPredictions = predictions.filter(p => {
        const [x, y, width, height] = p.bbox;
        const centerX = (x + width / 2) / videoWidth;
        const centerY = (y + height / 2) / videoHeight;
        
        return centerX >= region.xMin && centerX <= region.xMax &&
               centerY >= region.yMin && centerY <= region.yMax;
      });

      if (regionPredictions.length > 0) {
        const best = regionPredictions.reduce((a, b) => 
          a.score > b.score ? a : b
        );
        
        const [x, y, width, height] = best.bbox;
        
        feet.push({
          id: `region-${region.side}`,
          side: region.side,
          confidence: best.score,
          boundingBox: { x, y, width, height },
          color: colors[region.side],
          source: 'region-detection'
        });
      }
    });

    return feet;
  }

  createDefaultRegions(frameWidth, frameHeight) {
    const { lowerLegMode, colors } = this.config;
    const { boundingBox } = lowerLegMode;
    
    const boxWidth = frameWidth * boundingBox.widthRatio;
    const boxHeight = frameHeight * boundingBox.heightRatio;
    
    return [
      {
        id: 'default-right',
        side: 'right',
        confidence: 0,
        boundingBox: {
          x: frameWidth * 0.15,
          y: frameHeight * 0.5,
          width: boxWidth,
          height: boxHeight
        },
        color: colors.uncertain,
        source: 'default-region',
        isDefault: true
      },
      {
        id: 'default-left',
        side: 'left',
        confidence: 0,
        boundingBox: {
          x: frameWidth * 0.6,
          y: frameHeight * 0.5,
          width: boxWidth,
          height: boxHeight
        },
        color: colors.uncertain,
        source: 'default-region',
        isDefault: true
      }
    ];
  }

  smoothResults(results) {
    if (!results || !results.feet) return results;

    this.detectionHistory.push(results.feet);
    
    if (this.detectionHistory.length > this.maxHistoryLength) {
      this.detectionHistory.shift();
    }

    if (this.detectionHistory.length < 2) {
      return results;
    }

    // Average bounding boxes over recent frames
    const smoothedFeet = results.feet.map(foot => {
      const historicalMatches = this.detectionHistory
        .flat()
        .filter(h => h.side === foot.side && !h.isDefault);

      if (historicalMatches.length < 2) {
        return foot;
      }

      const avgBox = {
        x: historicalMatches.reduce((sum, h) => sum + h.boundingBox.x, 0) / historicalMatches.length,
        y: historicalMatches.reduce((sum, h) => sum + h.boundingBox.y, 0) / historicalMatches.length,
        width: historicalMatches.reduce((sum, h) => sum + h.boundingBox.width, 0) / historicalMatches.length,
        height: historicalMatches.reduce((sum, h) => sum + h.boundingBox.height, 0) / historicalMatches.length
      };

      return {
        ...foot,
        boundingBox: avgBox,
        smoothed: true
      };
    });

    return {
      ...results,
      feet: smoothedFeet
    };
  }

  drawDetections(ctx, results, options = {}) {
    if (!results || !results.feet) return;

    const {
      drawBoxes = true,
      drawLabels = true,
      drawCenterPoint = true,
      lineWidth = 3,
      fontSize = 14,
      showConfidence = true,
      showSource = false
    } = options;

    results.feet.forEach(foot => {
      const { boundingBox, color, side, confidence, isDefault, source } = foot;

      if (drawBoxes) {
        ctx.strokeStyle = color;
        ctx.lineWidth = lineWidth;
        
        // Dashed line for default/uncertain regions
        if (isDefault) {
          ctx.setLineDash([5, 5]);
        } else {
          ctx.setLineDash([]);
        }
        
        ctx.strokeRect(
          boundingBox.x,
          boundingBox.y,
          boundingBox.width,
          boundingBox.height
        );
        
        ctx.setLineDash([]);
      }

      if (drawLabels) {
        let label = side.charAt(0).toUpperCase() + side.slice(1);
        
        if (showConfidence && confidence > 0) {
          label += ` ${Math.round(confidence * 100)}%`;
        }
        
        if (isDefault) {
          label += ' (?)';
        }
        
        if (showSource && source) {
          label += ` [${source}]`;
        }

        ctx.font = `bold ${fontSize}px Arial`;
        const textWidth = ctx.measureText(label).width;

        // Label background
        ctx.fillStyle = isDefault ? 'rgba(255,255,0,0.8)' : color;
        ctx.fillRect(
          boundingBox.x,
          boundingBox.y - fontSize - 8,
          textWidth + 12,
          fontSize + 8
        );

        // Label text
        ctx.fillStyle = '#000';
        ctx.fillText(label, boundingBox.x + 6, boundingBox.y - 6);
      }

      if (drawCenterPoint) {
        const centerX = boundingBox.x + boundingBox.width / 2;
        const centerY = boundingBox.y + boundingBox.height / 2;
        
        ctx.beginPath();
        ctx.arc(centerX, centerY, 6, 0, 2 * Math.PI);
        ctx.fillStyle = color;
        ctx.fill();
        
        // Crosshair
        ctx.strokeStyle = '#000';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(centerX - 10, centerY);
        ctx.lineTo(centerX + 10, centerY);
        ctx.moveTo(centerX, centerY - 10);
        ctx.lineTo(centerX, centerY + 10);
        ctx.stroke();
      }
    });
  }

  dispose() {
    if (this.detector && this.detector.dispose) {
      this.detector.dispose();
      this.detector = null;
    }
    
    this.customDetectors.forEach(detector => {
      if (detector.dispose) detector.dispose();
    });
    this.customDetectors.clear();
    
    this.isInitialized = false;
    this.currentModel = null;
    this.listeners.clear();
    this.detectionHistory = [];
  }

  getStatus() {
    return {
      isInitialized: this.isInitialized,
      isLoading: this.isLoading,
      currentModel: this.currentModel,
      config: this.config,
      registeredDetectors: Array.from(this.customDetectors.keys())
    };
  }
}

export default new DetectionService();